import React, { useCallback, useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { getInvoicesPaged, bulkApprove, resolveDuplicate, money, baseMoney, STATUS_META } from "@/lib/apiClient";
import { ConfidenceBadge, ConfidenceBar } from "@/components/ConfidenceBadge";
import { useApp } from "@/context/AppContext";
import { cn } from "@/lib/utils";
import { toast } from "sonner";
import { AlertTriangle, FileText, UploadCloud, Filter, Loader2, Building2, X, Copy, UserCheck, CalendarClock, CheckCheck, Square, CheckSquare, CheckCircle2, Ban, ArrowUpRight, BadgeCheck } from "lucide-react";
import { ProviderLogo } from "@/components/ProviderLogo";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";

const PAGE_SIZE = 25;
const dueLabel = (dueDays) => {
  if (dueDays < 0) return `${-dueDays}d overdue`;
  if (dueDays === 0) return "Due today";
  return `Due in ${dueDays}d`;
};

const FILTERS = [
  { key: "flagged", label: "Needs Review" },
  { key: "mine", label: "Assigned to me" },
  { key: "duplicates", label: "Possible duplicates" },
  { key: "all", label: "All" },
  { key: "auto_coded", label: "Auto-Coded" },
  { key: "approved", label: "Approved" },
  { key: "rejected", label: "Rejected" },
];

const filterParams = (filter, user) => {
  if (filter === "flagged") return { needs_review: true };
  if (filter === "mine") return { assigned_to: user };
  if (filter === "all") return {};
  if (filter === "duplicates") return { duplicates: true };
  return { status: filter };
};

const DuplicateActions = ({ inv, onOpen, onDecided }) => {
  const [busy, setBusy] = useState(null);
  const decide = async (e, decision) => {
    e.stopPropagation();
    setBusy(decision);
    try {
      await onDecided(inv, decision);
    } finally {
      setBusy(null);
    }
  };
  const m = inv.possible_duplicate;
  return (
    <div className="mt-3 flex flex-wrap items-center gap-2 rounded-lg border border-amber-500/30 bg-amber-500/5 px-3 py-2" onClick={(e) => e.stopPropagation()} data-testid={`dup-actions-${inv.id}`}>
      <span className="text-xs text-muted-foreground flex-1 min-w-[12rem]">
        {m.reason} —{" "}
        <button className="font-mono text-primary hover:underline inline-flex items-center gap-0.5" onClick={(e) => { e.stopPropagation(); onOpen(m.invoice_id); }} data-testid={`dup-match-${inv.id}`}>{m.invoice_number || "view match"} <ArrowUpRight className="h-3 w-3" /></button>
      </span>
      <Button size="sm" variant="outline" className="h-7 text-xs border-emerald-500/40 text-emerald-700 hover:bg-emerald-500/10 dark:text-emerald-400" disabled={!!busy} onClick={(e) => decide(e, "keep")} data-testid={`dup-keep-${inv.id}`}>
        {busy === "keep" ? <Loader2 className="h-3 w-3 mr-1 animate-spin" /> : <CheckCircle2 className="h-3 w-3 mr-1" />} Keep
      </Button>
      <Button size="sm" variant="outline" className="h-7 text-xs border-red-500/40 text-red-600 hover:bg-red-500/10 dark:text-red-400" disabled={!!busy} onClick={(e) => decide(e, "reject")} data-testid={`dup-reject-${inv.id}`}>
        {busy === "reject" ? <Loader2 className="h-3 w-3 mr-1 animate-spin" /> : <Ban className="h-3 w-3 mr-1" />} Reject duplicate
      </Button>
    </div>
  );
};

const ISSUE_TEXT = { sync_failed: "could not be sent to", payment_failed: "paid in D20 but not recorded in", drift: "changed after approval in", pending: "waiting to be sent to" };
const issueLabel = (issue) => {
  const [p, code] = issue.split(":");
  return `${ISSUE_TEXT[code] || code} ${LEDGER_SHORT[p] || p}`;
};

const LEDGER_SHORT = { xero: "Xero", freshbooks: "FreshBooks", zoho: "Zoho", sage: "Sage" };

const paidBadge = (inv) => {
  if (!inv.payment) return null;
  const viaLedger = LEDGER_SHORT[inv.payment.method] ? inv.payment.method : Object.keys(LEDGER_SHORT).find((p) => String(inv[p]?.status || "").toLowerCase() === "paid");
  return { provider: viaLedger || null, label: viaLedger ? `Paid in ${LEDGER_SHORT[viaLedger]}` : "Paid", date: inv.payment.paid_at };
};

const PaidBadge = ({ inv }) => {
  const b = paidBadge(inv);
  if (!b) return null;
  return (
    <span data-testid={`paid-badge-${inv.id}`} data-paid-provider={b.provider || "d20"} title={`Paid ${b.date || ""}${b.provider ? ` · recorded in ${LEDGER_SHORT[b.provider]}` : ""}`}
      className="inline-flex items-center gap-1 rounded-md bg-emerald-500/15 px-2 py-0.5 text-[10px] font-semibold text-emerald-600 dark:text-emerald-400 whitespace-nowrap">
      {b.provider ? <ProviderLogo provider={b.provider} className="h-3 w-3" /> : <BadgeCheck className="h-3 w-3" />} {b.label}
    </span>
  );
};

const InvoiceCard = ({ inv, onOpen, selectable, selected, onToggle, onDuplicateDecided }) => {
  const meta = STATUS_META[inv.status];
  const flaggedLines = inv.line_items.filter((l) => l.confidence < 0.8);
  const dueDays = inv.due_date ? Math.ceil((new Date(inv.due_date) - new Date()) / 86400000) : null;
  return (
    <div
      data-testid={`review-card-${inv.id}`}
      onClick={() => onOpen(inv.id)}
      role="button"
      tabIndex={0}
      onKeyDown={(e) => e.key === "Enter" && onOpen(inv.id)}
      className={cn(
        "group relative min-w-0 text-left rounded-xl border bg-card p-5 hover:border-primary/50 hover:shadow-lg hover:shadow-primary/5 transition-all cursor-pointer",
        selected ? "border-primary bg-primary/5" : "border-border"
      )}
    >
      {selectable && (
        <label
          data-testid={`select-${inv.id}`}
          onClick={(e) => e.stopPropagation()}
          className="absolute -top-2 -left-2 flex h-7 w-7 items-center justify-center rounded-lg border border-border bg-card shadow-sm cursor-pointer hover:border-primary"
        >
          <Checkbox checked={selected} onCheckedChange={() => onToggle(inv.id)} aria-label="Select invoice" />
        </label>
      )}
      <div className="flex items-start justify-between gap-3">
        <div className="flex items-center gap-3 min-w-0">
          <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-muted text-muted-foreground">
            {inv.source === "pdf" ? <FileText className="h-5 w-5" /> : <UploadCloud className="h-5 w-5" />}
          </span>
          <div className="min-w-0">
            <div className="font-medium truncate">{inv.supplier || "Unknown"}</div>
            <div className="text-xs text-muted-foreground font-mono">{inv.invoice_number || "—"}</div>
          </div>
        </div>
        <div className="flex flex-col items-end gap-1">
          <span className={cn("rounded-lg border px-2.5 py-1 text-xs font-medium whitespace-nowrap", meta.cls)}>{meta.label}</span>
          <PaidBadge inv={inv} />
          {inv.status === "pending_review" && inv.review_due_at && new Date(inv.review_due_at) < new Date() && (
            <span data-testid={`sla-breached-${inv.id}`} className="rounded-md bg-red-500/15 px-2 py-0.5 text-[10px] font-semibold text-red-500 whitespace-nowrap">SLA breached · {Math.floor((Date.now() - new Date(inv.review_due_at)) / 36e5)}h</span>
          )}
          {inv.status === "pending_review" && inv.escalated && (
            <span data-testid={`escalated-${inv.id}`} className="rounded-md border border-red-500/40 px-2 py-0.5 text-[10px] font-semibold text-red-500 whitespace-nowrap">Escalated → {inv.escalated.to}</span>
          )}
        </div>
      </div>
      <div className="mt-4 flex items-center justify-between">
        <div>
          <div className="text-xs text-muted-foreground">Total</div>
          <div className="font-mono text-lg font-semibold">{money(inv.total, inv.currency)}</div>
          {baseMoney(inv) && <div className="text-[11px] font-mono text-muted-foreground" data-testid={`base-amount-${inv.id}`}>≈ {baseMoney(inv)} · rate {inv.fx_rate}</div>}
        </div>
        <ConfidenceBadge value={inv.min_confidence} />
      </div>
      <div className="mt-3">
        <ConfidenceBar value={inv.min_confidence} />
      </div>
      <div className="mt-3 flex flex-wrap gap-x-4 gap-y-1 text-xs">
        {flaggedLines.length > 0 && (
          <span className="text-amber-500 flex items-center gap-1.5">
            <AlertTriangle className="h-3.5 w-3.5" />
            {flaggedLines.length} line item{flaggedLines.length > 1 ? "s" : ""} need coding review
          </span>
        )}
        {inv.possible_duplicate && (
          <span className="text-amber-500 flex items-center gap-1.5">
            <Copy className="h-3.5 w-3.5" /> Possible duplicate
          </span>
        )}
        {inv.assigned_to && (
          <span className="text-primary flex items-center gap-1.5" data-testid="card-assigned">
            <UserCheck className="h-3.5 w-3.5" /> {inv.assigned_to}
          </span>
        )}
        {dueDays !== null && inv.status !== "rejected" && dueDays <= 7 && (
          <span data-testid="card-due" className={cn("flex items-center gap-1.5", dueDays < 0 ? "text-red-500" : "text-amber-500")}>
            <CalendarClock className="h-3.5 w-3.5" /> {dueLabel(dueDays)}
          </span>
        )}
      </div>
      {onDuplicateDecided && inv.possible_duplicate && <DuplicateActions inv={inv} onOpen={onOpen} onDecided={onDuplicateDecided} />}
    </div>
  );
};

export default function ReviewQueue() {
  const [params, setParams] = useSearchParams();
  const { user } = useApp();
  const supplier = params.get("supplier") || "";
  const issue = params.get("issue") || "";
  const [filter, setFilter] = useState(params.get("mine") ? "mine" : params.get("filter") || (supplier ? "all" : "flagged"));
  useEffect(() => { const f = params.get("filter"); if (f) setFilter(f); }, [params]);
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [more, setMore] = useState(false);
  const [selected, setSelected] = useState(() => new Set());
  const [approving, setApproving] = useState(false);
  const navigate = useNavigate();

  const selectable = filter !== "approved" && filter !== "rejected" && filter !== "duplicates";
  const openItems = items.filter((i) => i.status !== "approved" && i.status !== "rejected");
  const toggle = (id) => setSelected((s) => { const n = new Set(s); n.has(id) ? n.delete(id) : n.add(id); return n; });
  const selectAll = () => setSelected(new Set(openItems.map((i) => i.id)));
  const selectClean = () => setSelected(new Set(openItems.filter((i) => !i.flagged && !i.possible_duplicate).map((i) => i.id)));
  const selectedValue = items.filter((i) => selected.has(i.id)).reduce((a, i) => a + (i.total || 0), 0);
  const selectedRisky = items.filter((i) => selected.has(i.id) && (i.flagged || i.possible_duplicate)).length;

  const runBulkApprove = async () => {
    setApproving(true);
    try {
      const res = await bulkApprove([...selected], user);
      toast.success(`Approved ${res.approved_count} invoice(s) · ${money(res.approved_value)}${res.skipped.length ? ` · ${res.skipped.length} skipped` : ""}`);
      setSelected(new Set());
      await fetchPage(0);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Bulk approval failed");
    } finally {
      setApproving(false);
    }
  };

  const fetchPage = useCallback(
    async (offset) => {
      const res = await getInvoicesPaged({ ...filterParams(filter, user), supplier: supplier || undefined, issue: issue || undefined, limit: PAGE_SIZE, offset });
      setTotal(res.total);
      setItems((prev) => (offset === 0 ? res.items : [...prev, ...res.items]));
    },
    [filter, supplier, issue, user]
  );

  useEffect(() => {
    setLoading(true);
    setSelected(new Set());
    fetchPage(0).finally(() => setLoading(false));
  }, [fetchPage]);

  const decideDuplicate = async (inv, decision) => {
    try {
      await resolveDuplicate(inv.id, user, decision, null);
      setItems((prev) => prev.filter((i) => i.id !== inv.id));
      setTotal((t) => Math.max(0, t - 1));
      decision === "keep" ? toast.success(`${inv.invoice_number || "Invoice"} kept — not a duplicate`) : toast.error(`${inv.invoice_number || "Invoice"} rejected as a duplicate`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not save the decision");
    }
  };

  const loadMore = async () => {
    setMore(true);
    await fetchPage(items.length);
    setMore(false);
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="font-heading text-2xl sm:text-3xl font-semibold tracking-tight">Review Queue</h1>
          <p className="text-muted-foreground mt-1 text-sm sm:text-base">
            Low-confidence invoices (&lt;80%) that need a human decision before posting.
          </p>
        </div>
        <Button onClick={() => navigate("/approve")} data-testid="review-approve-mode-btn" className="gap-2"><CheckCircle2 className="h-4 w-4" /> Approve one by one</Button>
      </div>

      <div className="flex items-center gap-2 flex-wrap">
        <Filter className="h-4 w-4 text-muted-foreground" />
        {FILTERS.map((f) => (
          <button
            key={f.key}
            data-testid={`filter-${f.key}`}
            onClick={() => setFilter(f.key)}
            className={cn(
              "rounded-lg border px-3 py-1.5 text-sm font-medium transition-colors",
              filter === f.key ? "border-primary bg-primary/10 text-primary" : "border-border text-muted-foreground hover:text-foreground hover:bg-muted"
            )}
          >
            {f.label}
          </button>
        ))}
        {issue && (
          <span data-testid="issue-filter-chip" className="ml-2 inline-flex items-center gap-1.5 rounded-lg border border-amber-500/40 bg-amber-500/10 px-3 py-1.5 text-sm text-amber-700 dark:text-amber-400">
            <AlertTriangle className="h-3.5 w-3.5" /> {issueLabel(issue)}
            <button data-testid="clear-issue-filter" onClick={() => setParams({})} className="ml-1 hover:text-foreground" aria-label="Clear issue filter">
              <X className="h-3.5 w-3.5" />
            </button>
          </span>
        )}
        {supplier && (
          <span data-testid="supplier-filter-chip" className="ml-2 inline-flex items-center gap-1.5 rounded-lg border border-primary/40 bg-primary/10 px-3 py-1.5 text-sm text-primary">
            <Building2 className="h-3.5 w-3.5" /> {supplier}
            <button data-testid="clear-supplier-filter" onClick={() => setParams({})} className="ml-1 hover:text-foreground" aria-label="Clear supplier filter">
              <X className="h-3.5 w-3.5" />
            </button>
          </span>
        )}
        <span className="ml-auto text-xs text-muted-foreground font-mono" data-testid="queue-count">
          {loading ? "…" : `${items.length} of ${total}`}
        </span>
      </div>

      {loading && (
        <div className="flex justify-center py-24">
          <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
        </div>
      )}
      {!loading && items.length === 0 && (
        <div className="rounded-xl border border-dashed border-border bg-card p-10 text-center">
          <AlertTriangle className="h-10 w-10 mx-auto text-muted-foreground/50" />
          <p className="mt-3 font-medium">{filter === "duplicates" ? "No duplicates waiting for a decision" : "Nothing here"}</p>
          <p className="text-sm text-muted-foreground">{filter === "duplicates" ? "Every suspected duplicate has been kept or rejected." : "No invoices match this filter."}</p>
        </div>
      )}
      {!loading && items.length > 0 && filter === "duplicates" && (
        <p className="text-xs text-muted-foreground flex items-center gap-1.5" data-testid="duplicates-hint"><Copy className="h-3.5 w-3.5 text-amber-500" /> {total} suspected duplicate{total === 1 ? "" : "s"} waiting for a decision — Keep clears the flag, Reject duplicate closes the invoice.</p>
      )}
      {!loading && items.length > 0 && (
        <>
          {selectable && openItems.length > 0 && (
            <div className="flex flex-wrap items-center gap-2 text-xs" data-testid="bulk-toolbar">
              <button data-testid="select-all-btn" onClick={selectAll} className="inline-flex items-center gap-1 rounded-md border border-border px-2 py-1 text-muted-foreground hover:text-foreground hover:bg-muted">
                <CheckSquare className="h-3.5 w-3.5" /> Select all open ({openItems.length})
              </button>
              <button data-testid="select-clean-btn" onClick={selectClean} className="inline-flex items-center gap-1 rounded-md border border-emerald-500/40 px-2 py-1 text-emerald-500 hover:bg-emerald-500/10">
                <CheckCheck className="h-3.5 w-3.5" /> Select clean only (no flags / duplicates)
              </button>
              {selected.size > 0 && (
                <button data-testid="clear-selection-btn" onClick={() => setSelected(new Set())} className="inline-flex items-center gap-1 rounded-md px-2 py-1 text-muted-foreground hover:text-foreground">
                  <Square className="h-3.5 w-3.5" /> Clear
                </button>
              )}
            </div>
          )}
          <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
            {items.map((inv) => (
              <InvoiceCard
                key={inv.id}
                inv={inv}
                onOpen={(id) => navigate(`/invoice/${id}`)}
                selectable={selectable && inv.status !== "approved" && inv.status !== "rejected"}
                selected={selected.has(inv.id)}
                onToggle={toggle}
                onDuplicateDecided={filter === "duplicates" ? decideDuplicate : undefined}
              />
            ))}
          </div>
          {items.length < total && (
            <div className="flex justify-center pt-2">
              <Button data-testid="load-more-btn" variant="outline" onClick={loadMore} disabled={more}>
                {more && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
                Load {Math.min(PAGE_SIZE, total - items.length)} more
              </Button>
            </div>
          )}
          {selected.size > 0 && (
            <div data-testid="bulk-approve-bar" className="sticky bottom-4 z-30 mx-auto max-w-2xl rounded-xl border border-primary/40 bg-card/95 backdrop-blur-xl shadow-2xl shadow-primary/10 px-4 py-3 flex flex-wrap items-center justify-between gap-3">
              <div className="text-sm">
                <span className="font-semibold" data-testid="selected-count">{selected.size}</span> selected ·{" "}
                <span className="font-mono">{money(selectedValue)}</span>
                {selectedRisky > 0 && (
                  <span className="ml-2 inline-flex items-center gap-1 text-xs text-amber-500" data-testid="selected-risky">
                    <AlertTriangle className="h-3.5 w-3.5" /> {selectedRisky} flagged or possible duplicate
                  </span>
                )}
              </div>
              <div className="flex items-center gap-2">
                <Button variant="ghost" size="sm" onClick={() => setSelected(new Set())} disabled={approving}>Cancel</Button>
                <Button data-testid="bulk-approve-btn" size="sm" className="bg-emerald-600 hover:bg-emerald-700 text-white" onClick={runBulkApprove} disabled={approving}>
                  {approving ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <CheckCheck className="h-4 w-4 mr-2" />}
                  Approve {selected.size} selected as {user}
                </Button>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
}
